import { createSelector } from 'reselect'
import { EventStatus } from '../../../models/Event'
import dateFormatter from '../../../utils/DateFormat'
import { IStore } from '../..'

export function getRequestStatus(state: IStore) {
  return state.object.settingsRequest
}

export function isUserSettingsSet(state: IStore) {
  return state.object.userObject.profile !== undefined
}

export function getUserSettings(state: IStore) {
  return state.object.userObject.settings
}

export function getUserProfile(state: IStore) {
  return state.object.userObject.profile
}

export const getIsMultiPriceEnabled = (state: IStore) =>
  state.object.userObject.settings.pricelist_multi
export const getIsMultiPriceAvailable = (state: IStore) =>
  state.object.userObject.settings.pricelist_multi_can_switch

export function getUserSummary(state: IStore) {
  return state.object.userObject.settings.summary
}

export const getPrepaidTypeLabel = createSelector(
  [getUserSettings],
  settings => settings.defaultPrepaidType,
)

export function getTags(state: IStore) {
  return state.object.tags
}

export const getCleaning = (state: IStore) =>
  state.object.userObject.settings.serviceCleaning
export const getBedding = (state: IStore) =>
  state.object.userObject.settings.serviceBedding
export const getTowels = (state: IStore) =>
  state.object.userObject.settings.serviceTowels

export const getTowelsStatus = (state: IStore) =>
  state.object.userObject.settings.serviceTowelsStatus
export const getBeddingStatus = (state: IStore) =>
  state.object.userObject.settings.serviceBeddingStatus
export const getCleaningStatus = (state: IStore) =>
  state.object.userObject.settings.serviceCleaningStatus

export const getCleaningDays = (state: IStore) =>
  state.object.userObject.settings.serviceCleaningDays
export const getTowelsDays = (state: IStore) =>
  state.object.userObject.settings.serviceTowelsDays
export const getBeddingDays = (state: IStore) =>
  state.object.userObject.settings.serviceBeddingDays

export const getSettingsCleaning = createSelector(
  [getCleaning, getCleaningStatus, getCleaningDays],
  (d, s, w) => ({
    days: d,
    on: !!(s || d),
    status: s,
    weekDays: w,
  }),
)

export const getSettingsBedding = createSelector(
  [getBedding, getBeddingStatus, getBeddingDays],
  (d, s, w) => ({
    days: d,
    on: !!(s || d),
    status: s,
    weekDays: w,
  }),
)

export const getSettingsTowels = createSelector(
  [getTowels, getTowelsStatus, getTowelsDays],
  (d, s, w) => ({
    days: d,
    on: !!(s || d),
    status: s,
    weekDays: w,
  }),
)

const fixHourOrder = (format: string): string => {
  if (format.trim() === 'mm:HH') return 'HH:mm'
  if (format.trim() === 'mm:hh') return 'hh:mm a'
  return format
}

export const getDateTimeFormat = createSelector(
  [getUserSettings],
  settings => {
    const hour =
      undefined !== settings.dateFormat && settings.dateFormat.hour !== ''
        ? dateFormatter('', settings.dateFormat.hour)
        : dateFormatter('', 'GG:mm')
    return {
      format:
        undefined !== settings.dateFormat
          ? dateFormatter(settings.dateFormat.date, settings.dateFormat.hour)
          : dateFormatter('dd/MM/yyyy', 'GG:mm'),
      formatDate:
        undefined !== settings.dateFormat
          ? dateFormatter(settings.dateFormat.date, '')
          : dateFormatter('dd/MM/yyyy', ''),
      formatHours:
        undefined !== settings.dateFormat && settings.dateFormat.hour !== ''
          ? hour.split(':')[0] === 'hh'
            ? `${hour} A`
            : fixHourOrder(hour)
          : dateFormatter('', 'GG:mm'),
    }
  },
)

export const getReservationLabelPattern = createSelector(
  [getUserSettings, getDateTimeFormat],
  (settings, dateTimeFormat) => ({
    ...dateTimeFormat,
    // Was: '#Source.of.booking.origin# #Guest.name#' — BED-40785: origin icon not tied to label pattern
    pattern: settings.eventLabel || '#Guest.name#',
  }),
)

export const getCalendarPreferences = createSelector(
  [getUserSettings],
  settings => ({
    colors: {
      [EventStatus.NoPayment]:
        settings.customReservationStatus.colors[EventStatus.NoPayment],
      [EventStatus.Advance]:
        settings.customReservationStatus.colors[EventStatus.Advance],
      [EventStatus.Full]:
        settings.customReservationStatus.colors[EventStatus.Full],
      [EventStatus.Canceled]:
        settings.customReservationStatus.colors[EventStatus.Canceled],
      [EventStatus.Unavailable]:
        settings.customReservationStatus.colors[EventStatus.Unavailable],
    },
    daynightFrom: settings.daynightFrom,
    daynightTo: settings.daynightTo,
    labels: {
      [EventStatus.NoPayment]:
        settings.customReservationStatus.names[EventStatus.NoPayment],
      [EventStatus.Advance]:
        settings.customReservationStatus.names[EventStatus.Advance],
      [EventStatus.Full]:
        settings.customReservationStatus.names[EventStatus.Full],
      [EventStatus.Canceled]:
        settings.customReservationStatus.names[EventStatus.Canceled],
      [EventStatus.Unavailable]:
        settings.customReservationStatus.names[EventStatus.Unavailable],
    },
    shape: settings.eventShape,
  }),
)

export function isSettingsAccepted(state: IStore): boolean {
  return state.object.settingsRequest.acceptedCount > 0
}
